/**
 * Espaces de milliers à l'affichage : 4512354 se lit 4 512 354.
 *
 * Règle d'affichage, posée UNE fois ici et branchée à chaque rendu que l'élève
 * lit (MathText, texte enrichi, HTML + KaTeX, impression, phrases à trous,
 * expressions numériques). Les données ne bougent pas : la question, les
 * choix et la bonne réponse restent stockés collés, la correction compare ces
 * valeurs-là (cf. questionUtils, espaces entre chiffres ignorés en strict).
 *
 * Décision du 2026-09-06 (épreuve : quelle erreur coûte le moins cher ?) :
 * on n'audite pas 102 générateurs, on espace partout ce qui est un entier de
 * 4 chiffres et plus, et on protège ce qui n'est manifestement pas une
 * quantité. L'exception, elle, n'est connue que de l'AUTEUR : un générateur
 * la déclare avec `brut(n)` (generators/utils.js → <span class="nb-brut">),
 * et ce module la respecte. Les gardes de mots ci-dessous restent un filet
 * pour les questions saisies à la main ; elles ne s'étendent pas, un cas
 * nouveau se déclare à la source.
 *
 * Gardes, dans l'ordre :
 *   - partie décimale : 3,14159 ou 0,0001 ne changent pas ;
 *   - suite commençant par 0 : téléphone, code, jamais une quantité ;
 *   - suite juste après « n° », « code », « tél. » : un identifiant ;
 *   - année : 1000..2100 juste après « en », « année(s) », « depuis »,
 *     « avant », « après », « vers », « dès », « né(e) » (prose seulement :
 *     dans une formule, un nombre est une quantité).
 *
 * Ce module porte AUSSI le retour à la ligne, et ce n'est pas une
 * responsabilité de plus : c'est le CARACTÈRE posé entre deux groupes qui
 * autorise ou interdit au navigateur de couper la ligne, et c'est ici qu'il est
 * choisi. Tout nombre espacé par ce module est donc incassable depuis le
 * 2026-09-06. Cf. `normaliserGroupes` pour les nombres qui arrivent déjà
 * espacés à la main, la fuite constatée en projection le 2026-09-20.
 */

/** Espace fine insécable (U+202F), l'espace de milliers de la typographie
 *  française. TOUTE la protection du retour à la ligne tient à ce caractère :
 *  U+0020 se coupe, U+202F non, par définition. Verrouillé par un test. */
export const ESPACE_FINE = ' ';

const MOTS_ANNEE = /(?:^|[^\p{L}])(?:en|ann[ée]es?|depuis|avant|apr[èe]s|vers|d[èe]s|n[ée]e?)\s+$/iu;
// « n° 45123 », « code postal 97400 », « code secret : 1234 » : jusqu'à deux
// mots entre le mot-clé et le nombre.
const MOTS_IDENTIFIANT = /(?:^|[^\p{L}])(?:n[°º]|no|code|t[ée]l\.?)(?:\s+\p{L}+){0,2}\s*:?\s*$/iu;

/** Groupe une suite de chiffres par 3 depuis la droite : "4512354" → ["4","512","354"]. */
const grouper = (chiffres) => {
    const groupes = [];
    for (let fin = chiffres.length; fin > 0; fin -= 3) groupes.unshift(chiffres.slice(Math.max(0, fin - 3), fin));
    return groupes;
};

// Entier de 4 chiffres et plus, sans chiffre ni séparateur décimal juste
// avant (partie décimale), sans chiffre juste après.
const RE_ENTIER = /(?<![\d,.])\d{4,}(?!\d)/g;

/**
 * UN ESPACE ENTRE DEUX CHIFFRES N'EST JAMAIS UN ENDROIT OÙ COUPER UNE LIGNE.
 *
 * Deux constats prof du 2026-09-20, sur le vidéoprojecteur :
 *   - « Quelle décomposition est correcte pour 5 000 004 009 ? » (gen_598)
 *     coupé en plein nombre ;
 *   - « Place la virgule dans les chiffres 1 9 5 8 4 7 » (gen_596) coupé après
 *     le 8, la suite de chiffres finissant à cheval sur deux lignes.
 * Dans les deux cas le générateur pose l'espace lui-même, avec un U+0020
 * cassable, et ce module ne le voyait pas : il cherche quatre chiffres COLLÉS.
 *
 * Réparé ICI et non dans les générateurs, parce que les questions déjà
 * composées par les professeurs portent l'espace ordinaire dans leur texte
 * enregistré : corriger à l'AFFICHAGE les répare toutes, sans toucher aux
 * données, y compris celles tapées à la main dans la banque.
 *
 * Deux caractères, selon ce que l'espace sépare, parce que l'œil n'attend pas
 * la même chose :
 *   - groupes de trois chiffres (« 1 341 188 ») : espace FINE insécable, la
 *     convention française des milliers ;
 *   - tout le reste (« 1 9 5 8 4 7 ») : espace insécable de LARGEUR NORMALE,
 *     l'écart entre les chiffres reste exactement celui d'aujourd'hui, seule
 *     la coupure disparaît.
 * Aucun risque de confondre avec une liste : une liste porte un point-virgule,
 * un tiret ou des unités (règle du prof 2026-09-20).
 *
 * Cette règle ne change QUE le caractère de l'espace, jamais un chiffre : elle
 * ne peut donc pas fausser une valeur lue, et n'a besoin d'aucune garde. Même
 * doctrine que la correction, qui ignore déjà tout espace entre deux chiffres
 * (questionUtils) : ce qui ne compte pas pour comparer ne doit pas couper.
 *
 * Elle répare du même coup la détection des expressions (htmlAvecKatex) : sur
 * « 5 000 + 3 », l'espace ordinaire laissait le 5 hors de l'expression et la
 * ligne pouvait se rompre juste après lui.
 */
/** Espace insécable de largeur normale (U+00A0) : sépare des chiffres qui ne
 *  sont pas des groupes de milliers, sans rien changer à ce que l'œil voit. */
const ESPACE_INSECABLE = ' ';
/** Un nombre groupé par trois : 1 à 3 chiffres, puis des blocs de 3. */
const RE_GROUPES = /(?<!\d)\d{1,3}(?: \d{3})+(?!\d)/g;
/** Tout autre espace ordinaire entre deux chiffres. */
const RE_ESPACE_ENTRE_CHIFFRES = /(\d) (?=\d)/g;
const RE_ESPACE_ORDINAIRE = / /g;

const normaliserGroupes = (texte) => texte
    .replace(RE_GROUPES, (nombre) => nombre.replace(RE_ESPACE_ORDINAIRE, ESPACE_FINE))
    .replace(RE_ESPACE_ENTRE_CHIFFRES, `$1${ESPACE_INSECABLE}`);

/** Y a-t-il quelque chose à faire : un entier de 4 chiffres et plus, ou des
 *  chiffres déjà séparés à la main ? Des chiffres déjà séparés n'en ont plus
 *  quatre collés : sans ce second test, ils n'étaient jamais examinés. */
const aEspacer = (s) => /\d{4}/.test(s) || /\d \d/.test(s);

/**
 * Espace les milliers d'un fragment de TEXTE (sans balise). Sert de brique à
 * la version HTML ; utilisable seule sur un texte brut (MathText).
 * @param {string} texte
 * @param {{annees?: boolean}} [options] - `annees: false` désactive la garde d'année
 */
export const espacerMilliersTexte = (texte, options = {}) => {
    if (typeof texte !== 'string' || !aEspacer(texte)) return texte;
    const base = normaliserGroupes(texte);
    const garderAnnees = options.annees !== false;
    return base.replace(RE_ENTIER, (chiffres, index) => {
        if (chiffres[0] === '0') return chiffres;
        const avant = base.slice(Math.max(0, index - 16), index);
        if (MOTS_IDENTIFIANT.test(avant)) return chiffres;
        if (garderAnnees && chiffres.length === 4) {
            const valeur = Number(chiffres);
            if (valeur >= 1000 && valeur <= 2100 && MOTS_ANNEE.test(avant)) return chiffres;
        }
        return grouper(chiffres).join(ESPACE_FINE);
    });
};

// Un bloc <code> est du code, on n'y touche pas ; un <span class="nb-brut">
// est un nombre que l'AUTEUR a déclaré hors quantité (année, code : helper
// `brut()` des générateurs), on n'y touche pas non plus ; une balise commence
// par une lettre ou « / » (« 3 < 5 » en texte n'en est pas une).
const RE_MORCEAUX_HTML = /(<code[\s\S]*?<\/code>|<span class="nb-brut">[\s\S]*?<\/span>|<\/?[a-zA-Z][^>]*>)/;

/**
 * Espace les milliers du texte visible d'un fragment HTML : jamais une
 * balise, un attribut, un bloc <code> ni un nombre marqué `nb-brut`.
 * @param {string} html
 */
export const espacerMilliersHtml = (html) => {
    if (typeof html !== 'string' || !aEspacer(html)) return html;
    return html
        .split(RE_MORCEAUX_HTML)
        .map((morceau, i) => (i % 2 === 1 ? morceau : espacerMilliersTexte(morceau)))
        .join('');
};

// Dans une source KaTeX : pas après un chiffre, une virgule, un point, un
// « {,} » (virgule décimale LaTeX), ni juste après ^ ou _ (exposant, indice).
const RE_ENTIER_TEX = /(?<![\d,.]|\{,\}|[\^_]\{?)\d{4,}(?!\d)/g;

/**
 * Espace les milliers d'une source KaTeX avec l'espace fine LaTeX (`\,`).
 * Dans une formule, un nombre est une quantité : pas de garde d'année.
 * @param {string} tex
 */
export const espacerMilliersTex = (tex) => {
    if (typeof tex !== 'string' || !/\d{4}/.test(tex)) return tex;
    return tex.replace(RE_ENTIER_TEX, (chiffres) => (chiffres[0] === '0' ? chiffres : grouper(chiffres).join('\\,')));
};
